'use client'

import { useState } from 'react'
import { isNumber } from 'lodash'
import { Badge, Button, Icon, Image, Loader } from '@vinted/web-ui'
import { RotateRight16, X16 } from '@vinted/monochrome-icons'

import {
  ImageOrientation,
  MediaSelectPhotoOrientation,
  MediaUploadPhotoModel,
} from '@marketplace-web/item-upload/media-upload-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'

import { getPhotoThumbnail } from '../../utils'

import Sortable from '../SortableList'

enum ItemThumbnailSize {
  XLarge = 'thumb364x428',
}

type Props = {
  photo: MediaUploadPhotoModel
  orientation: ImageOrientation
  onDelete: (id: number) => void
  index: number
  imageAlt: string
  isControlsHidden: boolean
  enableRotation: boolean
  setPhotoOrientation: (photoOrientation: MediaSelectPhotoOrientation) => void
  showMainBadge?: boolean
}

const PhotoBox = ({
  photo,
  orientation,
  onDelete,
  index,
  imageAlt,
  isControlsHidden,
  enableRotation,
  setPhotoOrientation,
  showMainBadge = false,
}: Props) => {
  const [rotation, setRotation] = useState(0)

  const [isLoading, setIsLoading] = useState(true)
  const translate = useTranslate('item_upload.media_select')

  const handleRotateClockwise = (id: number) => () => {
    let newRotation: number
    let newOrientation = orientation

    if (!isNumber(orientation)) return

    if (rotation === 270) {
      newRotation = 0
    } else {
      newRotation = rotation + 90
    }

    if (orientation === ImageOrientation.Degree270) {
      newOrientation = ImageOrientation.Degree0
    } else {
      newOrientation += ImageOrientation.Degree90
    }

    setRotation(newRotation)
    setPhotoOrientation({ id, orientation: newOrientation })
  }

  const handleDelete = (id: number) => () => {
    onDelete(id)
  }

  const handleImageLoad = () => {
    setIsLoading(false)
  }

  const renderControls = () => {
    if (isControlsHidden || isLoading) return null

    return (
      <>
        <div className="photo-box__delete-button">
          <Button
            testId={`media-select-grid-delete-button-${index}`}
            icon={<Icon name={X16} color="greyscale-level-1" testId="x" />}
            size="small"
            inverse
            styling="filled"
            inline
            onClick={handleDelete(photo.id)}
            aria={{
              'aria-label': translate('a11y.delete_photo'),
            }}
          />
        </div>
        {enableRotation && (
          <div className="photo-box__action-button">
            <Button
              testId={`media-select-grid-rotate-button-${index}`}
              icon={<Icon name={RotateRight16} color="greyscale-level-1" testId="rotate-right" />}
              size="small"
              inverse
              styling="filled"
              inline
              onClick={handleRotateClockwise(photo.id)}
              aria={{
                'aria-label': translate('a11y.rotate_photo'),
              }}
            />
          </div>
        )}
      </>
    )
  }

  const renderMainBadge = () => {
    if (!showMainBadge) return null

    return (
      <div className="photo-box__main-badge">
        <Badge
          content={translate('main_badge_label')}
          testId={`media-select-main-badge-${index}`}
          styling={Badge.Styling.Filled}
        />
      </div>
    )
  }

  return (
    <div className="photo-box">
      <Sortable.Item id={photo.id} index={index}>
        <div className="photo-box__image-container" role="button" tabIndex={0}>
          <div
            data-testid={`image-wrapper-${index}`}
            style={{ transform: `rotate(${rotation}deg)` }}
          >
            <Image
              src={getPhotoThumbnail(photo, ItemThumbnailSize.XLarge) || photo.fullSizeUrl}
              scaling="cover"
              ratio="square"
              styling="rounded"
              onLoad={handleImageLoad}
              alt={imageAlt}
            />
          </div>
          {renderMainBadge()}
        </div>
      </Sortable.Item>
      {renderControls()}
      {isLoading && (
        <div data-testid={`media-select-grid-item-${index}-loader`} className="photo-box__loader">
          <Loader size="x-large" />
        </div>
      )}
    </div>
  )
}

export default PhotoBox
